import React from 'react';
import {
  Stack,
  Text,
  Heading,
  UnorderedList,
  ListItem,
  Link,
} from '@chakra-ui/react';
import Layout from '../../components/Layout';
import ContentWithStickySidebar from '../../components/shared/ContentWithStickySidebar';
import HeroWithBackgroundImage from '../../components/shared/HeroWithBackgroundImage';
import {
  SUPPORT_EMAIL,
  PRIVACY_EMAIL,
  ADDRESS_LINE_1,
  ADDRESS_LINE_2,
} from '../../constants';
import HeroImage from '../../images/TrustLPHeader.webp';

const PrivacyHeader = ({ children }: { children: string }) => (
  <Heading py="4" fontSize="xl">
    {children}
  </Heading>
);

const SupportLink = () => (
  <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
);

const PrivacyLink = () => (
  <Link href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</Link>
);

const Privacy = () => (
  <Layout title="Privacy">
    <HeroWithBackgroundImage
      imageUrl={HeroImage}
      backgroundColor="'rgba(255,255,255,.55)'"
      title="HOVER Privacy Policy"
      description="Last Revised: October 12, 2020"
    />
    <ContentWithStickySidebar>
      <Stack spacing="3">
        <Text as="span">
          HOVER is a platform that allows individuals and businesses to create
          virtual accounts for homes, including accurate, digital 3D models of a
          property. Our users either own, reside in, or otherwise have a
          personal connection with a property or are Hover Inc. business
          partners that provide services or supplies to a property.{' '}
        </Text>
        <Text as="span">HOVER users:</Text>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              Are homeowners or current residents (either a “Resident”) of a
              subject property with HOVER
            </span>
          </ListItem>
          <ListItem ml="14">
            <span>
              Are contractors who create property estimates based on a HOVER 3D
              model
            </span>
          </ListItem>
          <ListItem ml="14">
            <span>
              Are insurers who underwrite property insurance policies or process
              property damage claims for their clients
            </span>
          </ListItem>
        </UnorderedList>

        <Text as="span">
          This Privacy Policy explains how we collect, use, share and protect
          personal data that is collected by HOVER to provide these and other
          services. It applies to the HOVER service (also referred to as
          “Service”), which includes our website at{' '}
          <Link href="https://hover.to/">www.hover.to</Link>, our mobile
          application, and any other HOVER asset that links to this Privacy
          Policy.
        </Text>
        <Text as="span">
          If you are a business using HOVER as part of a paid relationship
          between Hover Inc. and one of our partners, an enterprise agreement
          likely applies to the personal data collected through your use of
          HOVER that allows for sharing of data collected from your account with
          that business partner. You should contact your Hover Inc. partner to
          understand what additional policies apply to your personal data.
        </Text>
        <Text as="span">
          Personal information is always governed by the rights of the Resident
          it relates to. For some accounts, the way data is collected or
          received may result in personal information of another individual in
          your account. If that other individual is a Resident, they will always
          retain rights over that information regardless of account ownership.
          For example, if you are part of a business and create a model for a
          homeowner, that homeowner cannot be denied access to contents of your
          account that relate to information you collected from or about them.{' '}
        </Text>
        <PrivacyHeader>Personal information we collect</PrivacyHeader>
        <Text as="span">
          We collect personal information directly from you, from HOVER users
          that add a property to their account in furtherance of working with a
          Resident, and automatically through your use of our Service.
        </Text>
        <Text as="p">
          <strong>Categories of personal information we may collec</strong>
          <span>
            <strong>t:</strong> Information about the property (such as postal
            address), information about uploaded images (such as the geolocation
            of the camera when a picture is taken), contact information about
            the property’s resident (such as name or email address), and
            information about a business performing the data collection (such as
            the organization name and contact information of its
            representative(s)).
          </span>
        </Text>
        <Text as="p">
          <strong>When you create an account</strong>
          <span>
            : A Resident or business must create an account to use HOVER. When
            you do so, we collect and store personal data you share with us,
            such as your name and email address, the property&#8217;s postal
            address and associated contact information you upload, as well as
            any additional information you add to your profile or information
            about properties you add to the account.
          </span>
        </Text>
        <Text as="p">
          <strong>
            When another HOVER user uploads information about your property
          </strong>
          <span>
            <strong>:</strong> HOVER works with many businesses that provide
            property services including contractors, assessors, and insurers.
            They use HOVER by adding properties to their account and storing
            information associated with these properties including names, email
            addresses, residential addresses and images of the home.{' '}
          </span>
        </Text>
        <Text as="p">
          <strong>When images are added to an account</strong>
          <span>
            <strong>:</strong> We collect and store images uploaded to HOVER as
            well as metadata and sensor information from your device (for
            example, image metadata and the location where the image was taken)
            in order to create a 3D model of a property.
          </span>{' '}
          Please take care to ensure the images you upload to HOVER do not
          contain any information that itself can identify you or any other
          individual<span>.</span>
        </Text>
        <Text as="p">
          <strong>When you pay for services</strong>
          <span>
            <strong>:</strong> We use a third-party service to process credit
            card and payment information for any payments made on our website.
            We do not access or store credit card data.
          </span>
        </Text>
        <Text as="p">
          <strong>When you use the HOVER Service</strong>
          <span>
            <strong>:</strong> We collect information about how you use the
            service whether you are logged into a HOVER account or just visiting
            our website. This may include log data (such as your device ID, IP
            address, browser information, device hardware, and software type),
            usage data (such as which pages you click on or how you use our
            mobile app), precise geo-location information, and Cookie Data. We
            may also periodically obtain both personal and non-personal
            information about you from other businesses, business partners,
            contractors, and other third parties.
          </span>
        </Text>
        <PrivacyHeader>How we use personal information</PrivacyHeader>
        <Text as="span">
          We process personal data in order to provide the HOVER services, for
          example:
        </Text>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              To allow you to log into and access your HOVER account, profile,
              and property information, to process payments, or to fulfill our
              obligations in our Terms of Use.
            </span>
          </ListItem>
        </UnorderedList>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              To improve, personalize, develop and perform analytics on the
              HOVER platform and services.
            </span>
          </ListItem>
        </UnorderedList>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              To offer features related to a property and to make a property
              available to others on the HOVER platform.
            </span>
          </ListItem>
        </UnorderedList>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              To determine a location for the property that doesn’t include your
              personal information.
            </span>
          </ListItem>
        </UnorderedList>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              To connect you with other HOVER users and Hover Inc. business
              partners when you choose to do so.
            </span>
          </ListItem>
        </UnorderedList>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              To contact you about your HOVER account, in response to a customer
              support request, and to offer you additional services, unless you
              opt-out of marketing communications.
            </span>
          </ListItem>
        </UnorderedList>

        <UnorderedList>
          <ListItem ml="14">
            <span>Determining relevant content to display to you.</span>
          </ListItem>
        </UnorderedList>

        <PrivacyHeader>When we share your information</PrivacyHeader>
        <Text as="span">
          We only share personal data in the following circumstances:
        </Text>
        <Text as="p">
          <strong>With your consent:</strong>
          <span>
            {' '}
            Personal information you upload directly to HOVER may be shared with
            third parties you authorize us to share data with such as a
            Resident, home improvement company or professional you purchase
            services from, or a social media site. Your personal information
            will be covered by those parties’ privacy policies.
          </span>
        </Text>
        <Text as="p">
          <strong>With the consent of a HOVER user</strong>
          <span>
            <strong>:</strong> Personal information related to a property that
            is uploaded by a HOVER User, with that user’s consent, may be shared
            with additional parties they authorize in order to provide services
            to that property.
          </span>
        </Text>
        <Text as="p">
          <strong>
            With a Hover Inc. partner under an enterprise agreement:
          </strong>
          <span>
            {' '}
            If your use of HOVER is being sponsored and/or paid for by a Hover
            Inc. partner, including if you are using a HOVER App sponsored by a
            partner, HOVER provides your personal information related to your
            use of HOVER to that Partner, its affiliates and/or applicable
            service providers. The Partner will have the ability to access,
            disclose, restrict, monitor, compile information and generate
            reports from your information and your general usage of the HOVER
            Platform and may use the information for their own business and
            marketing purposes, as described in the enterprise agreement between
            Hover Inc. and that Partner.{' '}
          </span>
        </Text>
        <Text as="p">
          <strong>
            If you do not agree to the sharing of your information or do not
            wish to be contacted by the Hover Inc. Partner, or its affiliates or
            service providers, do not use HOVER in this manner.
          </strong>
        </Text>
        <Text as="p">
          <strong>For processing purposes:</strong>
          <span>
            {' '}
            We transfer personal data to service providers who process it for
            us, based on our instructions, and in compliance with this policy
            and any other appropriate confidentiality and security measures.
            These partners provide us with services globally, including customer
            support, image processing, 3D model creation, information
            technology, payments, sales, marketing, data analysis, research, and
            surveys.
          </span>
        </Text>
        <Text as="p">
          <strong>In response to legal process:</strong>
          <span>
            {' '}
            We may disclose the information we collect from you in order to
            comply with valid legal processes, such as in response to a court
            order or a subpoena. Our policy is to notify you of legal process
            seeking access to your information unless we are prohibited by law
            from doing so. In cases where a court order specifies a
            non-disclosure period, we provide delayed notice after the
            expiration of the non-disclosure period.
          </span>
        </Text>
        <Text as="p">
          <strong>To protect us and others:</strong>
          <span>
            {' '}
            We may disclose the information we collect from you where we believe
            it is necessary to investigate, prevent, or take action regarding
            illegal activities, suspected fraud, situations involving potential
            threats to the safety of any person, violations of our Terms of Use
            or this Policy, or as evidence in litigation in which HOVER is
            involved.
          </span>
        </Text>
        <Text as="p">
          <strong>If our business changes</strong>
          <span>
            <strong>:</strong> If we are involved in a merger, acquisition, or
            sale of assets, we will continue to take measures to protect the
            confidentiality of personal information and give affected users
            notice before transferring any personal information to a new entity.
          </span>
        </Text>
        <PrivacyHeader>Third Party Tools and Cookies</PrivacyHeader>
        <Text as="p">
          <strong>Third-Party Tools:</strong>
          <span>
            {' '}
            We use automated devices and applications to operate and evaluate
            usage of our Service. We use these tools to help us improve the
            Service, performance, and user experiences. Among these tools are
            Mixpanel, Google Analytics and Branch.{' '}
          </span>
        </Text>

        <UnorderedList>
          <ListItem ml="14">
            <span>
              You may opt-out of Mixpanel’s analytics tracking by visiting{' '}
            </span>
            <Link href="https://mixpanel.com/optout">
              <span>https://mixpanel.com/optout</span>
            </Link>
            <span>
              . If you choose to use the Mixpanel opt-out, you will need to
              access the opt-out on each device you use.{' '}
            </span>
          </ListItem>
          <ListItem ml="14">
            <span>
              You may opt-out of Google Analytics by downloading the Google
              Analytics opt-out tool at{' '}
            </span>
            <Link href="https://tools.google.com/dlpage/gaoptout">
              <span>https://tools.google.com/dlpage/gaoptout</span>
            </Link>
            <span>. </span>
          </ListItem>
          <ListItem ml="14">
            <span>
              We use information from your browser or device to allow Branch to
              connect users with the HOVER app. You can opt-out of Branch by
              visiting{' '}
            </span>
            <Link href="https://branch.app.link/optout">
              <span>https://branch.app.link/optout</span>
            </Link>
            <span>.</span>
          </ListItem>
        </UnorderedList>

        <Text as="p">
          <strong>Cookies</strong>
          <Text as="span">
            <strong>:</strong> Cookies are small text files used to store data
            for a variety of purposes.{' '}
          </Text>
        </Text>
        <Text as="p">
          <Text as="span">
            The HOVER mobile app and website utilize functional cookies to
            enable the HOVER services and enhance the user experience such as
            maintaining account settings, login and authentication during
            logged-in periods. These cookies do not interact with browsing
            activity on non-HOVER platforms such as third party websites.
          </Text>
        </Text>
        <Text as="p">
          <Text as="span">
            The HOVER website utilizes, when enabled, advertising cookies to
            inform targeted advertising initiatives. These cookies further track
            advertising performance to deliver relevant HOVER products and
            services through third party platforms.
          </Text>
        </Text>
        <Text as="p">
          <Text as="span">
            To learn more about the various cookies engaged by the HOVER
            services, and to control your cookie settings, visit the HOVER Trust
            Center.
          </Text>
        </Text>
        <Text as="p">
          <Text as="span">
            You can also manage your cookies locally by adjusting your browser
            settings, or you can opt-out of targeted advertising through cookies
            by visiting
          </Text>{' '}
          <Link href="https://networkadvertising.org/choices">
            <Text as="span">networkadvertising.org/choices</Text>
          </Link>{' '}
          <Text as="span">or</Text>{' '}
          <Link href="http://aboutads.info/choices">
            <Text as="span">aboutads.info/choices</Text>
          </Link>
          <Text as="span">.</Text>
          <Text as="span"> </Text>
        </Text>
        <PrivacyHeader>Third-Party Links</PrivacyHeader>
        <Text as="span">
          Our Service may contain links to third-party websites or social media
          outlets. Any access to and use of such linked websites is not governed
          by this Policy but instead is governed by the privacy policies of
          those third-party websites. We are not responsible for the information
          practices of such third-party websites.
        </Text>
        <PrivacyHeader>Security</PrivacyHeader>
        <Text as="span">
          We have implemented reasonable precautions and follow security best
          practices to protect the information we collect from loss, misuse, and
          unauthorized access, disclosure, alteration, and destruction. We
          deploy network security and data encryption standards that follow
          industry best practices. All network access to and from HOVER is
          secured using SSL. Our data storage systems are also encrypted to
          enforce an extra layer of security for your personal information.
          Please be aware that despite our best efforts, no data security
          measures can guarantee security.
        </Text>
        <Text as="span">
          You should take steps to protect against unauthorized access to your
          password, phone, and computer by, among other things, signing off
          after using a shared computer, choosing a robust password that nobody
          else knows or can easily guess, and keeping your log-in and password
          private. We are not responsible for any lost, stolen, or compromised
          passwords or for any activity on your account via unauthorized
          password activity.
        </Text>
        <PrivacyHeader>Data Retention and Account Deactivation</PrivacyHeader>
        <Text as="p">
          <Text as="span">
            You can choose to deactivate your account by contacting{' '}
          </Text>
          <SupportLink />
          <Text as="span">
            . After your account is deactivated, you will not be able to sign in
            to our website or access any of your personal information. We will
            still retain information associated with your account for analytical
            purposes and recordkeeping integrity, to allow you to reactivate
            your account, as well as to prevent fraud, collect any fees owed,
            enforce our Terms of Use, take actions we deem necessary to protect
            the integrity of our web site or our users, or take other actions
            otherwise permitted by law. In addition, if certain information has
            already been provided to third parties as described in this Privacy
            Policy, retention of that information will be subject to those third
            parties’ policies and rights.
          </Text>
        </Text>
        <Text as="span">
          If your personal information resides in an account, you may access and
          control certain aspects of that account. For example, if a 3D model of
          your home is created by a business, you may delete your personal
          information associated with that model, or share that model and its
          measurements with other businesses’ accounts. It’s your data.
        </Text>
        <Text as="p">
          <Text as="span">
            We retain personal information as long as you like. W
          </Text>
          <Text as="span">
            e do not delete information unless specifically requested to do so
            with a verified request.{' '}
          </Text>
        </Text>
        <Text as="p">
          Retention duration is to ensure we can fulfill consumer queries on
          what information we have collected, or to verify a deletion request.
          In some cases, we may de-identify, aggregate, or otherwise anonymize
          personal information, or retain solely for internal purposes pursuant
          to California Consumer Privacy Act § 1798.105(d).
        </Text>
        <PrivacyHeader>Children</PrivacyHeader>
        <Text as="p">
          HOVER is targeted to adults, and we do not knowingly collect personal
          information from children under the age of 13. If you believe we have
          collected personal information from a child under the age of 13,
          please contact us immediately at <PrivacyLink />
        </Text>
        <PrivacyHeader>Your California Privacy Rights</PrivacyHeader>
        <Text as="p">
          This Privacy Policy and our practices comply with the California
          Consumer Privacy Act (CCPA), which requires that we provide California
          residents with notice that you have the right to:
        </Text>

        <UnorderedList>
          <ListItem ml="14">
            <Text as="span">opt-out of the sale of personal information,</Text>
          </ListItem>
          <ListItem ml="14">
            <Text as="span">
              know about personal information collected, disclosed or sold,
            </Text>
          </ListItem>
          <ListItem ml="14">
            <Text as="span">
              to request deletion of personal information, and{' '}
            </Text>
          </ListItem>
          <ListItem ml="14">
            <Text as="span">
              to be treated without discrimination should you exercise these
              rights
            </Text>
          </ListItem>
        </UnorderedList>

        <Text as="p">
          <Text as="span">
            To make a request under the California Consumer Privacy Act, please
            contact us at{' '}
          </Text>
          <PrivacyLink />
          <Text as="span"> with the Subject Line: CCPA.</Text>
        </Text>

        <PrivacyHeader>How To Make A Privacy Request</PrivacyHeader>
        <Text as="p">
          Please submit privacy requests using this{' '}
          <Link href="https://preferences.hover.to/privacy">Web Form.</Link>
        </Text>
        <Text as="p">
          You may also submit privacy requests or contact us with questions
          relating to this Privacy Policy by sending an email to our privacy
          team at <PrivacyLink /> or by calling our toll-free number
          (866-665-0340).
        </Text>

        <Text as="p">You may also write to us at the address below.</Text>

        <PrivacyHeader>Contact us</PrivacyHeader>
        <Text as="p">
          <Text as="span">
            You may contact us with any questions relating to this Privacy
            Policy by contacting us at{' '}
          </Text>
          <PrivacyLink />
        </Text>
        <Text as="p">
          <Text as="span">HOVER, Inc.</Text>
        </Text>
        <Text as="p">
          <Text as="span">{ADDRESS_LINE_1}</Text>
        </Text>
        <Text as="p">
          <Text as="span">{ADDRESS_LINE_2}</Text>
        </Text>
      </Stack>
    </ContentWithStickySidebar>
  </Layout>
);

export default Privacy;
